import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { LANGUAGE_NATIVE_NAMES, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../lib/i18n/config';

interface NavbarProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

export function Navbar({ currentLanguage, onLanguageChange }: NavbarProps) {
  const { t } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const [isInfoMenuOpen, setIsInfoMenuOpen] = useState(false);

  const languageFlags: Record<SupportedLanguage, string> = {
    en: '🇺🇸',
    ja: '🇯🇵',
    'zh-Hans': '🇨🇳',
    ko: '🇰🇷',
    es: '🇪🇸',
  };

  const languages = SUPPORTED_LANGUAGES.map((code) => ({
    code,
    name: LANGUAGE_NATIVE_NAMES[code],
    flag: languageFlags[code],
  }));

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  const primaryLinks = [
    { to: '/date-difference', label: t('nav.dateDifference') },
    { to: '/add-subtract', label: t('nav.addSubtract') },
    { to: '/business-days', label: t('nav.businessDays') },
  ];

  const toolLinks = [
    { to: '/weekday', label: t('nav.weekday') },
    { to: '/week-number', label: t('nav.weekNumber') },
    { to: '/time-duration', label: t('nav.timeDuration') },
  ];

  const infoLinks = [
    { to: '/use-cases', label: t('nav.useCases') },
    { to: '/other-tools', label: t('nav.otherToolsPage') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const dropdownClass = 'absolute mt-3 rounded-2xl border border-blue-100 bg-white p-2 shadow-[0_18px_50px_rgba(15,23,42,0.14)] z-[9999]';
  const linkClass = 'rounded-xl px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700';
  const menuItemClass = 'block w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-blue-50 hover:text-blue-700';

  return (
    <nav className="sticky top-0 z-40 border-b border-blue-100/80 bg-white/95 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-20 items-center justify-between gap-4">
          <Link to="/" className="flex shrink-0 items-center gap-3 rounded-xl">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-[0_10px_22px_rgba(37,99,235,0.22)]" aria-hidden="true">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </span>
            <span className="text-xl font-black tracking-tight text-slate-950 sm:text-2xl">DateCalculator</span>
          </Link>

          <div className="hidden min-w-0 items-center justify-center gap-1 md:flex">
            {primaryLinks.map((link) => (
              <Link key={link.to} to={link.to} className={linkClass}>
                {link.label}
              </Link>
            ))}

            <div className="relative">
              <button
                onClick={() => setIsToolsMenuOpen(!isToolsMenuOpen)}
                className={`${linkClass} flex items-center gap-1`}
                aria-haspopup="menu"
                aria-expanded={isToolsMenuOpen}
              >
                {t('nav.otherTools')}
                <svg className={`h-4 w-4 transition ${isToolsMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isToolsMenuOpen && (
                <div className={`${dropdownClass} left-0 w-56`} role="menu">
                  {toolLinks.map((link) => (
                    <Link key={link.to} to={link.to} onClick={() => setIsToolsMenuOpen(false)} className={menuItemClass}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setIsInfoMenuOpen(!isInfoMenuOpen)}
                className={`${linkClass} flex items-center gap-1`}
                aria-haspopup="menu"
                aria-expanded={isInfoMenuOpen}
              >
                {t('nav.info')}
                <svg className={`h-4 w-4 transition ${isInfoMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isInfoMenuOpen && (
                <div className={`${dropdownClass} left-0 w-52`} role="menu">
                  {infoLinks.map((link) => (
                    <Link key={link.to} to={link.to} onClick={() => setIsInfoMenuOpen(false)} className={menuItemClass}>
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:block">
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
                aria-label={t('common.changeLanguage')}
              >
                <span className="text-lg">{currentLang.flag}</span>
                <span>{currentLang.name}</span>
                <svg className={`h-4 w-4 transition ${isLangMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isLangMenuOpen && (
                <div className={`${dropdownClass} right-0 w-52`} role="menu">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        onLanguageChange(lang.code);
                        setIsLangMenuOpen(false);
                      }}
                      className={`w-full rounded-xl px-3 py-2 text-left text-sm font-medium transition ${
                        currentLanguage === lang.code ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'
                      }`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="rounded-xl border border-blue-100 bg-white p-2.5 text-slate-700 shadow-sm md:hidden"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="border-t border-blue-100 bg-white md:hidden">
          <div className="mx-auto max-h-[calc(100vh-5rem)] max-w-7xl space-y-2 overflow-y-auto px-4 py-4">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex w-full items-center justify-between rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm font-semibold text-slate-700"
              aria-label={t('common.changeLanguage')}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{currentLang.flag}</span>
                {currentLang.name}
              </span>
              <svg className={`h-4 w-4 transition ${isLangMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isLangMenuOpen && (
              <div className="rounded-2xl border border-blue-100 bg-white p-2 shadow-sm">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      onLanguageChange(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    className={`w-full rounded-xl px-3 py-2 text-left text-sm font-medium transition ${
                      currentLanguage === lang.code ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </button>
                ))}
              </div>
            )}

            {[...primaryLinks, ...toolLinks].map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setIsMobileMenuOpen(false)} className="block rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                {link.label}
              </Link>
            ))}

            <div className="px-4 pt-3 text-xs font-bold uppercase text-slate-400">{t('nav.info')}</div>
            {infoLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setIsMobileMenuOpen(false)} className="block rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
